<template>
    <div>
        <template v-if="!$_seekerFieldByCode('profile_picture').is_hidden">
            <ProfileAvatar
                :label="$_seekerFieldByCode('profile_picture').label"
                :disabled="$_seekerFieldByCode('profile_picture').read_only"
            />
        </template>

        <ValidationObserver
            ref="profileForm"
            tag="form"
            @submit.stop.prevent="updateForm"
            class="af-profile-form"
        >
            <div class="afu-grid md:afu-grid-cols-2 afu-gap-6 afu-mt-5 afu-mb-7">
                <template v-if="!$_seekerFieldByCode('first_name').is_hidden">
                    <ValidationProvider
                        name="First name"
                        rules="required"
                        v-slot="{ errors }"
                        class="af-profile-firstName"
                    >
                        <C1FormInput
                            id="firstName"
                            :label="$_seekerFieldByCode('first_name').label || $t('label.first_name')"
                            label-for="firstName"
                            v-model="user.first_name"
                            :is-invalid="!!errors[0]"
                            :invalid-feedback="errors[0]"
                            :disabled="$_seekerFieldByCode('first_name').read_only"
                            name="firstName"
                            :placeholder="$t('label.enter_value', {value: $t('label.first_name')})"
                            @blur="updateForm"
                        />
                    </ValidationProvider>
                </template>

                <template v-if="!$_seekerFieldByCode('last_name').is_hidden">
                    <C1FormInput
                        id="lastName"
                        :label="$_seekerFieldByCode('last_name').label || $t('label.last_name')"
                        label-for="lastName"
                        v-model="user.last_name"
                        name="lastName"
                        :placeholder="$t('label.enter_value', {value: $t('label.last_name')})"
                        :disabled="$_seekerFieldByCode('last_name').read_only"
                        @blur="updateForm"
                        class="af-profile-lastName"
                    />
                </template>

                <template v-if="!$_seekerFieldByCode('email').is_hidden">
                    <C1FormInput
                        id="email"
                        :label="$_seekerFieldByCode('email').label || $t('label.email_address')"
                        label-for="email"
                        v-model="user.email"
                        name="email"
                        disabled
                        class="af-profile-email"
                    />
                </template>

                <template v-if="!$_seekerFieldByCode('contact_number').is_hidden">
                    <ValidationProvider
                        name="contactNumber"
                        rules="phone_number"
                        v-slot="{ errors }"
                        class="af-profile-contactNumber"
                    >
                        <C1FormInput
                            id="contactNumber"
                            :label="$_seekerFieldByCode('contact_number').label || $t('label.contact_number')"
                            label-for="contactNumber"
                            v-model="user.contact_number"
                            name="contactNumber"
                            :is-invalid="!!errors[0]"
                            :invalid-feedback="errors[0]"
                            :placeholder="$t('label.enter_value', {value: $t('label.contact_number')})"
                            :disabled="$_seekerFieldByCode('contact_number').read_only"
                            @blur="updateForm"
                        />
                    </ValidationProvider>
                </template>

                <template v-if="!$_seekerFieldByCode('gender').is_hidden">
                    <C1Select
                        v-model="user.gender"
                        :label="$_seekerFieldByCode('gender').label || $t('label.gender')"
                        label-for="gender"
                        :options="genderOptions"
                        :placeholder="$t('label.select_value', {value: $t('label.gender')})"
                        :disabled="$_seekerFieldByCode('gender').read_only"
                        @input="updateForm"
                    />
                </template>

                <template v-if="!$_seekerFieldByCode('dob').is_hidden">
                    <ValidationProvider
                        name="Date of birth"
                        v-slot="{ errors }"
                        class="af-profile-dob"
                    >
                        <C1FormInput
                            id="dob"
                            :label="$_seekerFieldByCode('dob').label || $t('label.dob')"
                            label-for="dob"
                            name="dob"
                            v-model="user.dob"
                            :is-invalid="!!errors[0]"
                            :invalid-feedback="errors[0]"
                            :max="getMaxDate"
                            type="date"
                            :placeholder="$t('label.select_value', {value: $t('label.dob')})"
                            :disabled="$_seekerFieldByCode('dob').read_only"
                            @blur="updateForm"
                        />
                    </ValidationProvider>
                </template>

                <template v-if="!$_seekerFieldByCode('home_location').is_hidden">
                    <C1Typeahead
                        v-model="user.home_location"
                        :label="$_seekerFieldByCode('home_location').label || $t('label.home_location')"
                        label-for="homeLocation"
                        :options="homeLocationOptions"
                        :placeholder="$t('label.suburb_city_area')"
                        @search="homeLocationSearch"
                        @hit="selectHomeLocation"
                        :search-min-length="2"
                        :disabled="$_seekerFieldByCode('home_location').read_only"
                        class="af-profile-homeLocation"
                    >
                        <template v-slot:noResults>
                            {{ $t('profile.not_found_global_location') }}
                        </template>
                    </C1Typeahead>
                </template>

                <template v-if="!$_seekerFieldByCode('country_of_citizenship').is_hidden">
                    <C1Select
                        v-model="user.country_of_citizenship"
                        :label="$_seekerFieldByCode('country_of_citizenship').label || $t('label.country_of_citizenship')"
                        label-for="countryOfCitizenship"
                        :options="countries"
                        :placeholder="$t('label.select_value', {value: $t('label.country')})"
                        :disabled="$_seekerFieldByCode('country_of_citizenship').read_only"
                        @input="updateForm"
                    />
                </template>

                <template v-if="!$_seekerFieldByCode('emergency_contact_name').is_hidden">
                    <C1FormInput
                        id="emergencyContactName"
                        :label="$_seekerFieldByCode('emergency_contact_name').label || $t('label.emergency_contact_name')"
                        label-for="emergencyContactName"
                        v-model="user.emergency_contact_name"
                        name="emergencyContactName"
                        :placeholder="$t('label.enter_value', {value: $t('label.emergency_contact_name')})"
                        :disabled="$_seekerFieldByCode('emergency_contact_name').read_only"
                        @blur="updateForm"
                        class="af-profile-emergencyContactName"
                    />
                </template>

                <template v-if="!$_seekerFieldByCode('emergency_contact_number').is_hidden">
                    <ValidationProvider
                        name="emergencyContactNumber"
                        rules="phone_number"
                        v-slot="{ errors }"
                        class="af-profile-emergencyContactNumber"
                    >
                        <C1FormInput
                            id="emergencyContactNumber"
                            :label="$_seekerFieldByCode('emergency_contact_number').label || $t('label.emergency_contact_number')"
                            label-for="emergencyContactNumber"
                            v-model="user.emergency_contact_number"
                            name="emergencyContactNumber"
                            :is-invalid="!!errors[0]"
                            :invalid-feedback="errors[0]"
                            :placeholder="$t('label.enter_value', {value: $t('label.emergency_contact_number')})"
                            :disabled="$_seekerFieldByCode('emergency_contact_number').read_only"
                            @blur="updateForm"
                        />
                    </ValidationProvider>
                </template>

                <template v-if="!$_seekerFieldByCode('emergency_contact_rel').is_hidden">
                    <C1Select
                        v-model="user.emergency_contact_rel"
                        :label="$_seekerFieldByCode('emergency_contact_rel').label || $t('label.emergency_contact_rel')"
                        label-for="emergencyRel"
                        :options="emergencyRelOptions"
                        :placeholder="$t('label.select_value', {value: $t('label.emergency_contact_rel')})"
                        :disabled="$_seekerFieldByCode('emergency_contact_rel').read_only"
                        @input="updateForm"
                    />
                </template>

                <template v-if="!$_seekerFieldByCode('languages').is_hidden">
                    <SeekerAttributesTypeahead
                        :label="$_seekerFieldByCode('languages').label"
                        :attribute-id="24"
                        attribute-code="languages"
                        :disabled="$_seekerFieldByCode('languages').read_only"
                        class="af-profile-languages mb-0"
                    />
                </template>

                <!-- <ValidationProvider
                    name="websiteUrl"
                    rules="url"
                    v-slot="{ errors }"
                >
                    <C1FormInput
                        id="websiteUrl"
                        label="Website URL"
                        label-for="websiteUrl"
                        v-model="user.website_url"
                        :is-invalid="!!errors[0]"
                        :invalid-feedback="errors[0]"
                        name="websiteUrl"
                        placeholder="http(s)://"
                        @input="updateForm"
                    />
                </ValidationProvider> -->
            </div>
        </ValidationObserver>
        <div
            v-if="showDownloadBtn"
            class="af-profile-pdf-link afu-text-center afu-mt-5 md:afu-mt-0"
        >
            <a
                class="af-btn afu-cursor-pointer af-btn-primary afu-text-center afu-inline-flex afu-items-center afu-justify-center afu-border-btnWidth afu-border-solid focus:afu-outline-none afu-rounded-btn afu-transition afu-ease-in-out afu-duration-150 afu-text-neutral-light hover:afu-text-neutral-light focus:afu-text-neutral-light af-text--xl afu-font-semibold afu-border-primary hover:afu-border-primary-dark focus:afu-border-primary-dark afu-bg-primary active:afu-bg-primary-dark focus:afu-bg-primary-dark hover:afu-bg-primary-dark afu-p-btn afu-w-full"
                style="box-sizing: border-box;"
                @click.stop.prevent="downloadProfilePDF"
            >
                Download profile as PDF
            </a>
        </div>
    </div>
</template>

<script>
    import { mapState, mapActions, mapGetters } from 'vuex';
    import has from 'lodash/has';
    import debounce from 'lodash/debounce';
    import moment from 'moment/src/moment';
    import SeekerAttributesTypeahead from '@/apps/dashboard/001/components/SeekerAttributesTypeahead';
    import ProfileAvatar from '@/apps/dashboard/001/components/Profile/ProfileAvatar';
    import C1FormInput from '@/components/C1Components/C1FormInput';
    import C1Select from '@/components/C1Components/C1Select';
    import C1Typeahead from '@/components/C1Components/C1Typeahead';
    import '@/plugins/vee-validate';
    import { ValidationObserver, ValidationProvider } from 'vee-validate';
    import DashboardMixin from '@/apps/dashboard/mixins/DashboardMixin';

    export default {
        name: 'ProfileInfoForm',

        $_veeValidate: {
            validator: 'new',
        },

        components: {
            C1Typeahead,
            C1FormInput,
            ProfileAvatar,
            SeekerAttributesTypeahead,
            C1Select,
            ValidationObserver,
            ValidationProvider,
        },

        mixins: [DashboardMixin],

        props: {
            showDownloadBtn: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                user: {
                    first_name: '',
                    last_name: '',
                    gender: {},
                    dob: '',
                    contact_number: '',
                    home_location: '',
                    website_url: '',
                    emergency_contact_name: '',
                    emergency_contact_number: '',
                    emergency_contact_rel: {},
                },
                homeLocationSelected: {},
                homeLocationOptions: [],
                genderOptions: [
                    { label: 'Prefer not to say', id: 'Prefer not to say' },
                    { label: 'Female', id: 'Female' },
                    { label: 'Male', id: 'Male' },
                    { label: 'Non-binary', id: 'Non-binary' },
                ],
                emergencyRelOptions: [],
                countries: [],
            };
        },

        computed: {
            ...mapState({
                $_me: state => state.me.user,
            }),

            ...mapGetters('site', {
                $_seekerFieldByCode: 'seekerFieldByCode',
            }),

            getMaxDate() {
                return moment
                    .utc()
                    .local()
                    .format('YYYY-MM-DD');
            },
        },

        created() {
            this.user.first_name = this.$_me.first_name;
            this.user.last_name = this.$_me.last_name;
            this.user.email = this.$_me.email;
            this.user.gender = { label: this.$_me.gender, id: this.$_me.gender };
            this.user.dob = this.$_me.dob;
            this.user.contact_number = this.$_me.contact_number;
            this.user.home_location = this.$_me?.homeLocation?.label || '';
            this.user.website_url = this.$_me?.website_url || '';
            this.user.emergency_contact_name =
                this.$_me?.emergency_contact_name || '';
            this.user.emergency_contact_number =
                this.$_me?.emergency_contact_number || '';
            this.user.emergency_contact_rel = {
                label: this.$_me.emergency_contact_rel?.value,
                id: this.$_me.emergency_contact_rel?.id,
            };

            this.user.country_of_citizenship = {
                label: this.$_me.country_of_citizenship?.display_label,
                id: this.$_me.country_of_citizenship?.id,
            };
        
            this.$_getEmergencyRel().then(response => {
                this.emergencyRelOptions = response?.data.map(function(ele) {
                    return { label: ele.value, id: ele.id };
                });
            });

            this.$_getCountries().then(response => {
                this.countries = response?.data.map(function(ele) {
                    return { label: ele.display_label, id: ele.id };
                });
            });
        },

        methods: {
            ...mapActions('location', {
                $_getLocations: 'getLocations',
                $_getAllLocations: 'getAllLocations',
                $_getCountries: 'getCountries',
            }),

            ...mapActions('attribute', {
                $_getEmergencyRel: 'getEmergencyRel',
            }),

            ...mapActions('me', {
                $_updateMyDetails: 'updateMyDetails',
            }),

            searchLocation(query) {
                return this.$_getAllLocations({ query });
            },

            homeLocationSearch(query) {
                this.searchLocation(query).then(response => {
                    this.homeLocationOptions = response.data;
                });
            },

            updateForm: debounce(async function() {
                const validate = await this.$refs.profileForm.validate();
                if (!validate) {
                    return;
                }

                const data = {
                    first_name: this.user.first_name,
                    last_name: this.user.last_name,
                    gender: has(this.user, 'gender.label')
                        ? this.user.gender.label
                        : '',
                    dob: this.user.dob,
                    contact_number: this.user.contact_number,
                    home_location_id: this.homeLocationSelected?.id || '',
                    website_url: this.user.website_url,
                    emergency_contact_name: this.user.emergency_contact_name,
                    emergency_contact_number: this.user.emergency_contact_number,
                    emergency_contact_rel: this.user.emergency_contact_rel.id,
                    citizenship_country_id: this.user.country_of_citizenship.id,
                };
                this.$_updateMyDetails(data);
            }, 200),

            selectHomeLocation(location) {
                this.homeLocationSelected = location;
                this.updateForm();
            },
        },
    };
</script>
